<script setup lang="ts">
import { computed } from 'vue';
import SchoolClassEntityTab from 'src/modules/School/components/SchoolClassEntityTab/SchoolClassEntityTab.vue';
import type {
  ClassSectionProgressRow,
  SectionOption,
} from 'src/modules/School/composables/useSchoolData';

const props = withDefaults(
  defineProps<{
    assignedSections: SectionOption[];
    totalAssignedSections: number;
    search: string;
    isExpanded: boolean;
    selectedSectionDocumentId: string;
    selectedSectionName?: string;
    progressRows: ClassSectionProgressRow[];
    isProgressLoading: boolean;
    /** False in a locked semester: unassigning stays available, assigning does not. */
    canAdd?: boolean;
    /** Show the per-section "Preview" action (teacher walkthrough). */
    showPreview?: boolean;
    previewLoadingDocumentId?: string;
  }>(),
  {
    canAdd: true,
    selectedSectionName: undefined,
    showPreview: false,
    previewLoadingDocumentId: '',
  },
);

const emit = defineEmits<{
  (e: 'update:search', value: string): void;
  (e: 'toggle-expanded'): void;
  (e: 'select-section', sectionDocumentId: string): void;
  (e: 'preview-section', sectionDocumentId: string): void;
  (
    e: 'set-student-assignment',
    payload: { studentDocumentId: string; assigned: boolean },
  ): void;
}>();

const entities = computed(() =>
  props.assignedSections.map((section) => ({
    documentId: section.documentId,
    name: section.section_name,
    assignedCount: section.assignedCount,
    completedCount: section.completedCount,
  })),
);

const rows = computed(() =>
  props.progressRows.map((row) => ({
    student: row.student,
    enrollmentStatus: row.enrollmentStatus,
    assigned: row.assigned,
    userProgress: row.userSection,
  })),
);
</script>

<template>
  <SchoolClassEntityTab
    :entities="entities"
    :total-assigned="totalAssignedSections"
    :search="search"
    :is-expanded="isExpanded"
    :selected-document-id="selectedSectionDocumentId"
    :selected-name="selectedSectionName"
    :progress-rows="rows"
    :is-progress-loading="isProgressLoading"
    :can-add="canAdd"
    :show-preview="showPreview"
    :preview-loading-document-id="previewLoadingDocumentId"
    title-key="SECTIONS"
    selected-fallback-key="SECTIONS"
    select-prompt-key="SCHOOL_SELECT_ASSIGNED_COURSE_TO_VIEW_PROGRESS"
    @update:search="emit('update:search', $event)"
    @toggle-expanded="emit('toggle-expanded')"
    @select="emit('select-section', $event)"
    @preview="emit('preview-section', $event)"
    @set-student-assignment="emit('set-student-assignment', $event)"
  />
</template>
